import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  SimpleGrid,
  HStack,
  Divider,
} from "@chakra-ui/react";
import CustomInput from "components/common/customInput/CustomInput";
import ShadowBox from "components/common/ShadowBox/ShadowBox";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";

type WthTax = {
  _id?: string;
  value: string;
  label: string;
  country?: string;
  countryName?: string;
  process?: string;
  companyCode?: string;
  company?: string;

  taxCodes?: number[];

  tax1?: string;
  tax2?: string;
  tax3?: string;
  tax4?: string;
};

const initialForm: WthTax = {
  value: "",
  label: "",
  country: "",
  countryName: "",
  process: "",
  companyCode: "",
  company: "",

  tax1: "",
  tax2: "",
  tax3: "",
  tax4: "",
};

export default function WthTaxs(): JSX.Element {
  const { toastNotify } = useContext(AppContext);

  const [form, setForm] = useState<WthTax>(initialForm);
  const [list, setList] = useState<WthTax[]>([]);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [searchProcess, setSearchProcess] = useState("");
  const [searchCompanyCode, setSearchCompanyCode] = useState("");

  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>(undefined);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleChange = (name: keyof WthTax, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const fetchWthTaxs = async () => {
    try {
      const res: any = await appRequest("wthtax", "getAll", {
        page,
        limit,
        process: searchProcess || undefined,
        companyCode: searchCompanyCode || undefined,
      });

      if (res?.status === "success") {
        setList(res?.data?.data || []);
        setTotalPages(res?.data?.totalPages || 1);
      }
    } catch (err: any) {
      toastNotify({
        title: "Fetch failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    }
  };

  useEffect(() => {
    fetchWthTaxs();
  }, [page]);

  useEffect(() => {
    setPage(1);
    fetchWthTaxs();
  }, [searchProcess, searchCompanyCode]);

  const handleAdd = async () => {
    if (!form.value || !form.label) {
      toastNotify({
        title: "Validation error",
        message: "Value and Label are required",
        type: "error",
      });
      return;
    }

    const payload = {
      ...form,
      taxCodes: [
        Number(form.tax1 || 0),
        Number(form.tax2 || 0),
        Number(form.tax3 || 0),
        Number(form.tax4 || 0),
      ],
    };

    delete payload.tax1;
    delete payload.tax2;
    delete payload.tax3;
    delete payload.tax4;

    try {
      setLoading(true);

      if (editingId) {
        await appRequest("wthtax", "update", { id: editingId, ...payload });
      } else {
        await appRequest("wthtax", "create", payload);
      }

      toastNotify({
        title: "Success",
        message: editingId ? "Updated successfully" : "Saved successfully",
        type: "success",
      });

      setForm(initialForm);
      setEditingId(undefined);
      fetchWthTaxs();
      onClose();
    } catch (err: any) {
      toastNotify({
        title: "Operation failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id?: string) => {
    if (!id) return;
    try {
      setLoading(true);
      await appRequest("wthtax", "delete", { id });

      toastNotify({
        title: "Deleted",
        message: "Wth Tax removed successfully",
        type: "success",
      });

      fetchWthTaxs();
    } catch (err: any) {
      toastNotify({
        title: "Delete failed",
        message: err?.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ShadowBox>
      <Flex mb={4} align="center" justify="space-between">
        <Box fontWeight="bold" fontSize="lg">
          WTH Tax Codes Master
        </Box>

        <Button
          colorScheme="teal"
          onClick={() => {
            setForm(initialForm);
            setEditingId(undefined);
            onOpen();
          }}
        >
          Add
        </Button>
      </Flex>

      <HStack spacing={4} mb={4}>
        <CustomInput
          name="searchProcess"
          label="Process"
          value={searchProcess}
          onChange={(e: any) => setSearchProcess(e.target.value)}
        />

        <CustomInput
          name="searchCompanyCode"
          label="Company Code"
          value={searchCompanyCode}
          onChange={(e: any) => setSearchCompanyCode(e.target.value)}
        />
      </HStack>

      <Box mt={3} maxH="60vh" overflowY="auto">
        <Table size="sm" variant="striped">
          <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
            <Tr>
              <Th>Value</Th>
              <Th>Label</Th>
              <Th>Country</Th>
              <Th>Country Name</Th>
              <Th>Process</Th>
              <Th>Company</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>

          <Tbody>
            {list.map((item) => (
              <Tr key={item._id}>
                <Td>{item.value}</Td>
                <Td>{item.label}</Td>
                <Td>{item.country}</Td>
                <Td>{item.countryName}</Td>
                <Td>{item.process}</Td>
                <Td>{item.company}</Td>
                <Td>
                  <HStack>
                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="blue"
                      onClick={() => {
                        setForm({
                          ...item,
                          tax1: item?.taxCodes?.[0]?.toString() || "",
                          tax2: item?.taxCodes?.[1]?.toString() || "",
                          tax3: item?.taxCodes?.[2]?.toString() || "",
                          tax4: item?.taxCodes?.[3]?.toString() || "",
                        });
                        setEditingId(item._id);
                        onOpen();
                      }}
                    >
                      Edit
                    </Button>

                    <Button
                      size="xs"
                      variant="outline"
                      colorScheme="red"
                      onClick={() => handleDelete(item._id)}
                    >
                      Delete
                    </Button>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {/* MODAL */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6px)" />
        <ModalContent borderRadius="2xl" boxShadow="2xl">
          <ModalHeader fontSize="lg" fontWeight="600">
            {editingId ? "Edit WTH Tax Code" : "Add WTH Tax Code"}
          </ModalHeader>

          <Divider />

          <ModalBody py={5}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <CustomInput name="value" label="Value" value={form.value} onChange={(e: any) => handleChange("value", e.target.value)} />
              <CustomInput name="label" label="Label" value={form.label} onChange={(e: any) => handleChange("label", e.target.value)} />
              <CustomInput name="country" label="Country" value={form.country} onChange={(e: any) => handleChange("country", e.target.value)} />
              <CustomInput name="countryName" label="Country Name" value={form.countryName} onChange={(e: any) => handleChange("countryName", e.target.value)} />

              <Box>
                <Box fontSize="13px" fontWeight="500" mb={1}>
                  Process
                </Box>
                <select
                  style={{
                    width: "100%",
                    padding: "9px 12px",
                    borderRadius: "10px",
                    border: "1px solid #CBD5E0",
                  }}
                  value={form.process}
                  onChange={(e) => handleChange("process", e.target.value)}
                >
                  <option value="">Select Process</option>
                  <option value="domestic">Domestic</option>
                  <option value="marcan">Marcan</option>
                  <option value="tillomed">Tillomed</option>
                  <option value="emerging">Emerging</option>
                  <option value="zuventus">Zuventus</option>
                  <option value="foreign">Foreign</option>
                  <option value="provakil">Provakil</option>
                </select>
              </Box>

              <CustomInput name="companyCode" label="Company Code" value={form.companyCode} onChange={(e: any) => handleChange("companyCode", e.target.value)} />
              <CustomInput name="company" label="Company" value={form.company || ""} onChange={(e: any) => handleChange("company", e.target.value)} />

              {/* TAX GRID */}
              <SimpleGrid columns={4} spacing={3} gridColumn="1 / -1">
                <CustomInput name="tax1" label="Tax 1" type="number" value={form.tax1 || ""} onChange={(e: any) => handleChange("tax1", e.target.value)} />
                <CustomInput name="tax2" label="Tax 2" type="number" value={form.tax2 || ""} onChange={(e: any) => handleChange("tax2", e.target.value)} />
                <CustomInput name="tax3" label="Tax 3" type="number" value={form.tax3 || ""} onChange={(e: any) => handleChange("tax3", e.target.value)} />
                <CustomInput name="tax4" label="Tax 4" type="number" value={form.tax4 || ""} onChange={(e: any) => handleChange("tax4", e.target.value)} />
              </SimpleGrid>
            </SimpleGrid>
          </ModalBody>

          <Divider />

          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="teal" onClick={handleAdd} isLoading={loading}>
              {editingId ? "Update" : "Save"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </ShadowBox>
  );
}
